import { useEffect, useRef, useState } from "react";
const SOCKET_URL = `${process.env.REACT_APP_Socket_URL}`;

export const useSocket = (onLogMessage, onLogUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const wsRef = useRef(null);
  const eventListenersRef = useRef(new Map());
  const logQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
 
  // Add log
  const addLogMessage = (message) => {
    if (onLogMessage) onLogMessage(message);
    setDisplayedLogs((prev) => {
      const updated = [...prev, message];
      if (onLogUpdate) onLogUpdate(updated);
      return updated;
    });
  };

  // Process log queue to avoid overwhelming the UI
  const processLogQueue = () => {
    if (isProcessingRef.current || logQueueRef.current.length === 0) return;
    
    isProcessingRef.current = true;
    
    const processNext = () => {
      if (logQueueRef.current.length === 0) {
        isProcessingRef.current = false;
        return;
      }
      
      const message = logQueueRef.current.shift();
      addLogMessage(message);
      
      // Process next message after a short delay
      setTimeout(processNext, 100);
    };
    
    processNext();
  };

  // Socket.io-style event emitter functions
  const emit = (event, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = { event, data };
      wsRef.current.send(JSON.stringify(message));
      addLogMessage(`📤 Emitted ${event}: ${JSON.stringify(data)}`);
    } else {
      addLogMessage(`⚠️ Cannot emit ${event}: WebSocket not connected`);
    }
  };

  const on = (event, callback) => {
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, []);
    }
    eventListenersRef.current.get(event).push(callback);
  };

  const off = (event, callback) => {
    if (eventListenersRef.current.has(event)) {
      const listeners = eventListenersRef.current.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  };

  const once = (event, callback) => {
    const onceCallback = (...args) => {
      off(event, onceCallback);
      callback(...args);
    };
    on(event, onceCallback);
  };

  const sendQuery = (query) => {
    emit("ask", { query });
  };

  // Handle incoming messages and trigger event listeners
  const handleMessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      // console.log("dataValue",data)
      // console.log("data item", data && data.message)
      addLogMessage(data && data.message);
      
      // Handle different message formats
      if (data.event) {
        // Event-based message format
        const listeners = eventListenersRef.current.get(data.event) || [];
        listeners.forEach(callback => {
          try {
            callback(data.data);
          } catch (error) {
            console.error(`Error in event listener for ${data.event}:`, error);
          }
        });
      } else if (data.type) {
        // Type-based message format (for backward compatibility)
        const listeners = eventListenersRef.current.get(data.type) || [];
        listeners.forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error(`Error in event listener for ${data.type}:`, error);
          }
        });
      } else {
        // Raw message format
        const listeners = eventListenersRef.current.get('message') || [];
        listeners.forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in message listener:', error);
          }
        });
      }
    } catch (error) {
      // Handle non-JSON messages
      addLogMessage(`📩 Raw message: ${event.data}`);
      const listeners = eventListenersRef.current.get('message') || [];
      listeners.forEach(callback => {
        try {
          callback(event.data);
        } catch (err) {
          console.error('Error in message listener:', err);
        }
      });
    }
  };
 
  const connect = () => {
    if (wsRef.current) return;
 
    setIsConnecting(true);
    addLogMessage("Connecting to WebSocket");
 
    wsRef.current = new WebSocket(SOCKET_URL);
 
    wsRef.current.onopen = () => {
      setIsConnected(true);
      setIsConnecting(false);
      addLogMessage("WebSocket connected");
      
      // Send initial ping
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      } else {
        console.warn("WebSocket not ready yet");
      }
      
      // Trigger connect event listeners
      const listeners = eventListenersRef.current.get('connect') || [];
      listeners.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Error in connect listener:', error);
        }
      });
    };
 
    wsRef.current.onmessage = handleMessage;
 
    wsRef.current.onerror = (err) => {
      addLogMessage(`WebSocket error`);
      console.error("WebSocket error:", err);
      
      // Trigger error event listeners
      const listeners = eventListenersRef.current.get('error') || [];
      listeners.forEach(callback => {
        try {
          callback(err);
        } catch (error) {
          console.error('Error in error listener:', error);
        }
      });
    };
 
    wsRef.current.onclose = () => {
      setIsConnected(false);
      setIsConnecting(false);
      addLogMessage("WebSocket disconnected");
      wsRef.current = null;
      
      // Trigger disconnect event listeners
      const listeners = eventListenersRef.current.get('disconnect') || [];
      listeners.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Error in disconnect listener:', error);
        }
      });
    };
  };
 
  const disconnect = () => {
    wsRef.current?.close();
    wsRef.current = null;
    setIsConnected(false);
    setIsConnecting(false);
  };
 
  const send = (data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      addLogMessage(`Sent: ${JSON.stringify(data)}`);
    } else {
      addLogMessage("WebSocket not connected");
    }
  };

  // Auto-connect on mount
  useEffect(() => {
    connect();

    // Periodic check to ensure queue processing continues
    const intervalId = setInterval(() => {
      if (logQueueRef.current.length > 0 && !isProcessingRef.current) {
        processLogQueue();
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
      disconnect();
    };
  }, []);
 
  return {
    connected: isConnected,
    connecting: isConnecting,
    logs: displayedLogs,
    displayedLogs,
    connect,
    disconnect,
    send,
    // Socket.io-style functions
    emit,
    on,
    off,
    once,
    sendQuery,
  };
};
 